import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import GlobalSEO from './components/GlobalSEO';
import BlogSidebar from './components/BlogSidebar';
import Home from './pages/Home';
import HomeLoanCalculator from './pages/calculators/HomeLoanCalculator';
import EMICalculator from './pages/calculators/EMICalculator';
import TaxCalculator from './pages/calculators/TaxCalculator';
import SIPCalculator from './pages/calculators/SIPCalculator';
import CreditCardComparison from './pages/comparisons/CreditCardComparison';
import LoanComparison from './pages/comparisons/LoanComparison';
import InsuranceComparison from './pages/comparisons/InsuranceComparison';
import TaxSavingTips from './pages/guides/TaxSavingTips';
import InvestmentStrategies from './pages/guides/InvestmentStrategies';
import StockMarketBasics from './pages/guides/StockMarketBasics';
import AboutUs from './pages/info/AboutUs';
import Contact from './pages/info/Contact';
import PrivacyPolicy from './pages/info/PrivacyPolicy';
import TermsOfService from './pages/info/TermsOfService';
import Blog from './pages/Blog';
import BlogDetails from './pages/BlogDetails';
import PageViewTracker from './components/PageViewTracker';
import ScrollToTop from './components/ScrollToTop';

// Blog List Section Component
const BlogListSection = () => {
  const location = useLocation();
  
  // Different titles for different page types
  const getTitleAndDescription = () => {
    if (location.pathname.startsWith('/calculators/')) {
      return {
        title: '💡 Learn More About Financial Planning',
        description: 'Discover expert tips and strategies to make the most of your calculations'
      };
    } else if (location.pathname.startsWith('/comparisons/')) {
      return {
        title: '📊 Read Related Financial Insights',
        description: 'Make informed decisions with our expert financial guides and tips'
      };
    } else if (location.pathname.startsWith('/guides/')) {
      return {
        title: '📚 More Financial Education',
        description: 'Expand your financial knowledge with these related articles'
      };
    } else {
      return {
        title: '✨ Discover More Financial Tips',
        description: 'Get expert insights to improve your financial journey'
      };
    }
  };
  
  const { title, description } = getTitleAndDescription();
  
  return (
    <section className="bg-gradient-to-br from-blue-50 to-indigo-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {description}
          </p>
        </div>
        
        <BlogSidebar maxBlogs={6} />
        
        <div className="text-center mt-8">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            <span>View All Blog Posts</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

// Layout wrapper component to add blog list
const LayoutWrapper = () => {
  const location = useLocation();
  
  // Pages where we don't want to show the blog list
  const noBlogListPages = ['/', '/blog'];
  const shouldShowBlogList = !noBlogListPages.includes(location.pathname) && 
                             !location.pathname.startsWith('/blog/');

  return (
    <>
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<Home />} />
          
          {/* Calculator Routes */}
          <Route path="/calculators/home-loan" element={<HomeLoanCalculator />} />
          <Route path="/calculators/emi" element={<EMICalculator />} />
          <Route path="/calculators/tax" element={<TaxCalculator />} />
          <Route path="/calculators/sip" element={<SIPCalculator />} />
          
          {/* Comparison Routes */}
          <Route path="/comparisons/credit-cards" element={<CreditCardComparison />} />
          <Route path="/comparisons/loans" element={<LoanComparison />} />
          <Route path="/comparisons/insurance" element={<InsuranceComparison />} />
          
          {/* Guide Routes */}
          <Route path="/guides/tax-saving-tips" element={<TaxSavingTips />} />
          <Route path="/guides/investment-strategies" element={<InvestmentStrategies />} />
          <Route path="/guides/stock-market-basics" element={<StockMarketBasics />} />
          
          {/* Info Pages */}
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          
          {/* Blog Routes */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetails />} />
        </Routes>
      </main>

      {/* Blog List Section for all pages except home and blog pages */}
      {shouldShowBlogList && <BlogListSection />}
    </>
  );
};

function App() {
  useEffect(() => {
    // Register service worker
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
          })
          .catch((registrationError) => {
          });
      });
    }
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <GlobalSEO />
      <PageViewTracker />
      <div className="min-h-screen flex flex-col">
        <PWAInstallPrompt />
        <Navbar />
        <LayoutWrapper />
        <Footer />
      </div>
    </Router>
  );
}

export default App;